import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Star, Pencil, Lock, Quote } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { RATING_META } from '@/lib/hire-rating';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

/* ============================================================================
 * Brand feedback on ONE hire — admin read-only view
 * ============================================================================
 * The brand rates every finished hire out of 5 and can leave a private memo
 * (Brand-Dashboard → Hired Creators → HireFeedbackModal; written by the worker,
 * POST /api/hiring/hires/:id/feedback). Ops sees it here, per hire — not rolled
 * up on the creator — because that is the unit the brand rated: the same
 * creator can be a 5 on one campaign and a 2 on the next, and an average would
 * hide exactly the hire someone is looking at.
 *
 * Read-only by design. Admin has no business editing a brand's opinion of a
 * hire; if one has to go (abuse, mistaken identity), that's a deliberate DB
 * action, not a button on this page.
 *
 * The memo is brand-private — the creator can never read it (RLS keeps
 * cr_hire_feedback to brand members + admin). Don't paste it into a message to
 * the creator or surface it on their profile.
 * ========================================================================== */

type HireFeedback = {
  id: string;
  hire_id: string;
  rating: number;
  notes: string | null;
  submitted_by_email: string | null;
  updated_by_email: string | null;
  edit_count: number;
  created_at: string;
  updated_at: string;
};

/** Stars out of 5. Empty ones keep their outline so a 3 reads as "3 of 5". */
function Stars({ rating, className }: { rating: number; className: string }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map(n => (
        <Star
          key={n}
          className={n <= rating ? `h-5 w-5 ${className}` : 'h-5 w-5 text-muted-foreground/30'}
          fill={n <= rating ? 'currentColor' : 'none'}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

export function HireFeedbackCard({ paymentStatus, settledAt, creatorName, hireId }: {
  hireId: string;
  /** Creator payout status — 'paid' once ops records the Trolley payout (full
   *  or partial). The primary eligibility signal: a fully delivered hire
   *  reaches status 'completed' well before this flips, so status plays no
   *  part in feedbackOpen below (mirrors the worker's own gate). */
  paymentStatus: string;
  /** Set when ops closed the hire out short of full delivery with a refund —
   *  covers the one case a payout alone would miss (nothing owed to the
   *  creator, so paymentStatus can stay 'unpaid' forever). */
  settledAt: string | null;
  creatorName: string;
}) {
  const { data: feedback, isLoading } = useQuery<HireFeedback | null>({
    queryKey: ['hire-feedback', hireId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cr_hire_feedback')
        .select('id, hire_id, rating, notes, submitted_by_email, updated_by_email, edit_count, created_at, updated_at')
        // maybeSingle, not single: most hires have no feedback row, and that
        // isn't an error worth throwing on.
        .eq('hire_id', hireId)
        .maybeSingle();
      if (error) throw error;
      return (data as HireFeedback | null) ?? null;
    },
    enabled: !!hireId,
  });

  // Whether the brand OWES feedback yet — the same rule the worker enforces
  // (hireAcceptsFeedback in worker/src/hiring.ts). Gated on the money, not the
  // videos: 'completed' status lands well before the creator is actually paid.
  const feedbackOpen = paymentStatus === 'paid' || !!settledAt;
  const meta = feedback ? RATING_META[feedback.rating] : null;

  return (
    <Card className={`p-5 space-y-4 transition-shadow duration-200 ${meta ? `ring-1 ${meta.ring} hover:shadow-elevate-lg` : ''}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Star className="h-4 w-4 text-muted-foreground" />
        <h2 className="font-semibold text-sm">Brand Feedback</h2>
        {feedback ? (
          <Badge className={`border ${meta!.badge}`}>{feedback.rating}/5 · {meta!.label}</Badge>
        ) : feedbackOpen ? (
          <Badge className="bg-amber-100 text-amber-800 border border-amber-200">Awaiting rating</Badge>
        ) : (
          <Badge variant="outline" className="text-muted-foreground">Not open yet</Badge>
        )}
        {/* <span className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground">
          <Lock className="h-3 w-3" /> Never shown to the creator
        </span> */}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : feedback ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Score medallion — the number a brand actually gave, sized to
                anchor the card the way the badge chip alone couldn't. */}
            <div className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-full ring-4 ${meta!.ring} ${meta!.medallion}`}>
              <span className={`text-2xl font-bold leading-none tracking-tight ${meta!.score}`}>{feedback.rating}.0</span>
            </div>
            <div className="space-y-1.5">
              <Stars rating={feedback.rating} className={meta!.star} />
              <p className="text-sm text-muted-foreground">
                <span className={`font-semibold ${meta!.score}`}>{meta!.label}</span> · out of 5
              </p>
            </div>
          </div>

          {feedback.notes ? (
            <div className={`relative rounded-lg border-l-4 px-4 py-3 ${meta!.border} ${meta!.quoteBg}`}>
              <Quote className="absolute -top-2 left-3 h-4 w-4 fill-current text-muted-foreground/30" />
              <p className="text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap break-words">
                {feedback.notes}
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">No notes left with this rating.</p>
          )}

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t pt-3 text-xs text-muted-foreground">
            <span>
              Rated {format(new Date(feedback.created_at), 'MMM d, yyyy')}
              {feedback.submitted_by_email ? ` by ${feedback.submitted_by_email}` : ''}
            </span>
            {feedback.edit_count > 0 && (
              <span className="inline-flex items-center gap-1">
                <Pencil className="h-3 w-3" />
                Edited {format(new Date(feedback.updated_at), 'MMM d, yyyy')}
                {feedback.updated_by_email ? ` by ${feedback.updated_by_email}` : ''}
                {feedback.edit_count > 1 ? ` (${feedback.edit_count} revisions)` : ''}
              </span>
            )}
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          {feedbackOpen
            ? `This hire is finished and the brand hasn't rated ${creatorName} for it yet. The Hired Creators page keeps prompting them until they do.`
            : 'The brand rates a hire once the creator has been paid (or the hire was settled short with a refund) — nothing to show until then.'}
        </p>
      )}
    </Card>
  );
}
